import React from 'react';
import { Editor } from '@tinymce/tinymce-react';
import connectProps from 'shared/lib/connectProps';

import Fonts from 'shared/lib/Fonts';
import 'shared/lib/tinyMceParamsPlugin';
import 'fantastic/lib/tinymceColorpicker';
import directSettings from 'fantastic/lib/directSettings';
import { set } from 'react-hook-form';

const Tinymce = (props) => {
  const {
    disabled,
    html,
    onChange = (value) => {},
    onInit = (event, editor) => {},
    onActivate = () => {},
    onDeactivate = () => {},
    custom = {},
    promptActive = true,
    // onClickPrompt = () => {},
  } = props;

  const settings = directSettings();

  const generateSizes = () => {
    const from = 8;
    const to = 60;
    return [...Array(to - from + 1).keys()]
      .map((x) => x + from)
      .map((x) => `${x}px`)
      .join(' ');
  };

  const sizes = generateSizes();

  const initOptions = (options) => {
    return {
      skin: 'mdskin',
      statusbar: false,
      inline: true,
      content_style: 'div.mce-content-body { outline: none; }',
      menubar: false,
      convert_urls: false,
      resize: false,
      params_items: settings.recipient_parameters,
      language: i18n.locale,
      plugins:
        'textcolor colorpicker colorpicker2 link params lineheight tinymceEmoji nonbreaking paste advlist lists prompt',
      toolbar: [
        'bold italic underline strikethrough BullList NumList | alignleft aligncenter alignright alignjustify | link unlink params | forecolor2 backcolor2 nonbreaking tinymceEmoji',
        `${
          promptActive && settings.ai_copywriter ? 'prompt | ' : ''
        }formatselect removeformat | fontselect fontsizeselect lineheightselect`,
      ],
      // block_formats: 'Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3;Heading 4=h4;Heading 5=h5;Heading 6=h6',
      paste_as_text: true,
      paste_data_images: false,
      paste_block_drop: true,
      fontsize_formats: sizes,
      lineheight_formats: sizes,
      font_formats: Fonts.list()
        .map((x) => x.name + '=' + x.font)
        .join(';'),
      emoji_add_space: false,
      emoji_show_groups: true,
      emoji_show_subgroups: false,
      emoji_show_tab_icons: true,
      gecko_spellcheck: true,
      target_list: false,
      link_list: [
        { title: i18n.t('link_unsubscribe'), value: '[%unsubscribe_link%]' },
        { title: i18n.t('link_view'), value: '[%view_link%]' },
      ],
      anchor_bottom: false,
      anchor_top: false,
      setup: (editor) => {
        editor.on('init', (e) => onInit(e, editor));
        editor.on('ToggleSidebar', () => onDeactivate());
        // editor.on("openPrompt", () => onClickPrompt());

        editor.addButton('BullList', {
          type: 'splitbutton',
          icon: 'bullist list-tooltip',
          tooltip: i18n.t('list_bullet'),
          onclick: function () {
            editor.execCommand('InsertUnorderedList');
            document.querySelectorAll('.mce-menu').forEach((menu) => {
              if (menu.style.display !== 'none') menu.style.display = 'none';
            });

            if (editor.menu && typeof editor.menu.hideMenu === 'function') {
              editor.menu.hideMenu();
            }
          },
          menu: [
            {
              icon: 'fi fi-list-desc list-icon list-icon-i',
              onclick: function () {
                editor.execCommand('InsertUnorderedList', false, { 'list-style-type': 'disc' });
              },
            },
            {
              icon: 'fi fi-list-circle list-icon-i',
              onclick: function () {
                editor.execCommand('InsertUnorderedList', false, { 'list-style-type': 'circle' });
              },
            },
            {
              icon: 'fi fi-list-square list-icon-i',
              onclick: function () {
                editor.execCommand('InsertUnorderedList', false, { 'list-style-type': 'square' });
              },
            },
          ],
        });

        editor.addButton('NumList', {
          type: 'splitbutton',
          icon: 'numlist list-tooltip',
          tooltip: i18n.t('list_number'),
          onclick: function () {
            editor.execCommand('InsertOrderedList');
            document.querySelectorAll('.mce-menu').forEach((menu) => {
              if (menu.style.display !== 'none') menu.style.display = 'none';
            });

            if (editor.menu && typeof editor.menu.hideMenu === 'function') {
              editor.menu.hideMenu();
            }
          },
          menu: [
            {
              icon: 'fi fi-list-num list-icon list-icon-i',
              onclick: function () {
                editor.execCommand('InsertOrderedList', false, { 'list-style-type': 'decimal' });
              },
            },
            {
              icon: 'fi fi-list-num-lower-alpha list-icon-i',
              onclick: function () {
                editor.execCommand('InsertOrderedList', false, { 'list-style-type': 'lower-alpha' });
              },
            },
            {
              icon: 'fi fi-list-num-lower-greek list-icon-i',
              onclick: function () {
                editor.execCommand('InsertOrderedList', false, { 'list-style-type': 'lower-greek' });
              },
            },
            {
              icon: 'fi fi-list-num-lower-roman list-icon-i',
              onclick: function () {
                editor.execCommand('InsertOrderedList', false, { 'list-style-type': 'lower-roman' });
              },
            },
            {
              icon: 'fi fi-list-num-upper-alpha list-icon-i',
              onclick: function () {
                editor.execCommand('InsertOrderedList', false, { 'list-style-type': 'upper-alpha' });
              },
            },
            {
              icon: 'fi fi-list-num-upper-roman list-icon-i',
              onclick: function () {
                editor.execCommand('InsertOrderedList', false, { 'list-style-type': 'upper-roman' });
              },
            },
          ],
        });

        editor.on('NodeChange', function () {
          const isUnordered = editor.queryCommandState('InsertUnorderedList');
          const isOrdered = editor.queryCommandState('InsertOrderedList');

          const bullBtns = document.querySelectorAll('.mce-splitbtn:has(.mce-i-bullist)');
          const numBtns = document.querySelectorAll('.mce-splitbtn:has(.mce-i-numlist)');

          const node = editor.selection.getNode();
          const ul = editor.dom.getParent(node, 'ul');
          const ol = editor.dom.getParent(node, 'ol');
          let styleType = '';
          if (ul) styleType = editor.dom.getStyle(ul, 'listStyleType') || 'disc';
          if (ol) styleType = editor.dom.getStyle(ol, 'listStyleType') || 'decimal';

          const unorderedMap = {
            disc: 'fi-list-desc',
            circle: 'fi-list-circle',
            square: 'fi-list-square',
          };

          const orderedMap = {
            decimal: 'fi-list-num',
            'lower-alpha': 'fi-list-num-lower-alpha',
            'lower-greek': 'fi-list-num-lower-greek',
            'lower-roman': 'fi-list-num-lower-roman',
            'upper-alpha': 'fi-list-num-upper-alpha',
            'upper-roman': 'fi-list-num-upper-roman',
          };

          const highlightMenuItemByIcon = (iconClass) => {
            document.querySelectorAll('.mce-menu-item').forEach((item) => {
              const icon = item.querySelector('i');
              if (icon && icon.classList.contains(iconClass)) {
                item.classList.add('mce-active');
              }
            });
          };

          const highlightMenuButtons = () => {
            bullBtns.forEach((btn) => btn.classList.toggle('mce-active', isUnordered));
            numBtns.forEach((btn) => btn.classList.toggle('mce-active', isOrdered));

            document.querySelectorAll('.mce-menu-item').forEach((item) => {
              item.classList.remove('mce-active');
            });

            if (isUnordered && unorderedMap[styleType]) {
              bullBtns.forEach((btn) => btn.classList.add('mce-active'));
              highlightMenuItemByIcon(unorderedMap[styleType]);
            }
            if (isOrdered && orderedMap[styleType]) {
              numBtns.forEach((btn) => btn.classList.add('mce-active'));
              highlightMenuItemByIcon(orderedMap[styleType]);
            }
          };

          highlightMenuButtons();

          const splitButtons = document.querySelectorAll('.mce-splitbtn');
          splitButtons.forEach((btn) => {
            const openBtn = btn.querySelector('.mce-open');

            openBtn.addEventListener('click', () => {
              setTimeout(() => {
                highlightMenuButtons();
              }, 10);
            });
          });
        });
      },
      ...options,
    };
  };

  return (
    <Editor
      init={initOptions(custom)}
      initialValue={html}
      value={html}
      onEditorChange={(content, editor) => onChange(content)}
      onSelectionChange={onActivate}
      disabled={disabled}
    />
  );
};

export default Tinymce;
